<template>
  <SliderModalItem height="auto">
    <div class="price-filter-container">
      <div class="headline-m">{{ $t('products.filters.price') }}</div>
      <UiRangeSlider
        :has-inputs="true"
        :locale="getLocaleCode()"
        :min-range="resolvedPriceRange.minPrice"
        :max-range="resolvedPriceRange.maxPrice"
        :debounce-ms="2000"
        :selected-value="selectedValue"
        :to-reset-vals="toResetPriceRange"
        @range-resetted="emit('rangeResetted')"
        @values-selected="updateSelected($event)"
      />
    </div>
  </SliderModalItem>
</template>

<script lang="ts" setup>
import { type LocaleCodes } from '@sephora-asia/ui-range-slider/types'
import { type PriceRangeFilter } from '~/types/api/data/product/filter/filter.types'
import UiRangeSlider from '@sephora-asia/ui-range-slider/UiRangeSlider.vue'
import SliderModalItem from './slider-modal-item.vue'

interface Props {
  priceRangeFilter: PriceRangeFilter
  toResetPriceRange: boolean
}
const props = withDefaults(defineProps<Props>(), {
  toResetPriceRange: false,
})
interface Emits {
  (e: 'rangeResetted'): void
}
const emit = defineEmits<Emits>()

const { trackEvent } = useGtmTrack()
const { getAcceptLanguage } = useI18nLocale()
const { selectedFilterParams, updateSelectedPriceRange, resolvePriceRange } =
  useProductListingFiltersStore()

function getLocaleCode(): LocaleCodes {
  return getAcceptLanguage() as LocaleCodes
}

/**
 * ----- Computed -----
 */
const resolvedPriceRange = computed(() =>
  resolvePriceRange(props.priceRangeFilter)
)

// To sync with UiRangeSlider selectedValue with state
const selectedValue = computed(() => {
  return {
    min: selectedFilterParams.priceRange['min-price'],
    max: selectedFilterParams.priceRange['max-price'],
  }
})

function onPriceRangeTracking(minMaxValues: number[]) {
  if (
    minMaxValues[0] === +selectedValue.value.min &&
    minMaxValues[1] === +selectedValue.value.max
  ) {
    return
  }

  const { minPrice: limitMin, maxPrice: limitMax } = resolvedPriceRange.value
  const [minValue, maxValue] = minMaxValues

  const isInvalidMinValue = minValue < 0 || minValue <= limitMin
  const isInvalidMaxValue = maxValue < 0 || maxValue >= limitMax
  const cm8 = isInvalidMaxValue && isInvalidMinValue ? -1 : 1

  trackEvent({
    event: 'click',
    params: {
      event_category: 'Filters',
      event_action: 'Click price Filter',
      event_label: `from-${minValue}-to-${maxValue}`,
      cm8,
    },
  })
}

/**
 * ----- Methods -----
 */
function updateSelected(priceRange: number[]) {
  onPriceRangeTracking(priceRange)
  updateSelectedPriceRange(priceRange, resolvedPriceRange.value)
}
</script>

<style lang="scss" scoped>
.price-filter-container {
  width: 100%;
  padding-bottom: var(--size-typography-size-24);

  div {
    margin: var(--size-typography-size-14) 0;
  }
}
</style>
